import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import NiceModal, {useModal} from '@ebay/nice-modal-react';
import dayjs from 'dayjs';

import type {FileApiModel} from '@yourcoach/shared/api/media/file';
import type {GetComponentProps} from '@yourcoach/shared/utils/utility-types';

import {useI18n} from '@yourcoach/i18n';
import {Modal} from '@yourcoach/shared/components/Modal';
import {getFileSrc} from '@yourcoach/shared/utils/entity/file';
import {logger} from '@yourcoach/shared/utils/logger';
import {storageTokenManager} from '@yourcoach/shared/utils/storageToken';

import * as S from './styles';

const getFileSize = async (url: string) => {
  try {
    const response = await fetch(url, {
      // no-cors is required to get blob
      mode: 'no-cors',
    });

    const blob = await response.blob();

    return blob.size;
  } catch (e) {
    logger.error(e);

    return 0;
  }
};

const DEFAULT_RELOAD_TIMEOUT = 2000;

export const DocumentViewerModal = NiceModal.create<DocumentViewerModal.Props>(
  ({
    document,
    onAfterClose: onAfterCloseProp,
    renderFooter: renderFooterProp,
    signature,
    title,
  }) => {
    const modal = useModal();

    const {t} = useI18n();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isReady, setIsReady] = useState(false);

    const reloadInterval = useRef(0);
    const reloadTimeout = useRef(DEFAULT_RELOAD_TIMEOUT);

    const signedSrc = useMemo(() => {
      const fileSrc = getFileSrc(document);

      if (!fileSrc.url) {
        return;
      }

      const secureUrl = storageTokenManager.signUrl(fileSrc.url);

      return secureUrl;
    }, [document]);

    const src = useMemo(
      () =>
        signedSrc
          ? `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(
              signedSrc,
            )}`
          : undefined,
      [signedSrc],
    );

    useEffect(() => {
      const setup = async () => {
        if (!signedSrc) {
          return;
        }

        const fileSize = await getFileSize(signedSrc);

        // calc timeout based on file size in bytes with download speed 250kb/s
        const fileSizeInKb = fileSize / 1024;

        reloadTimeout.current = Math.max(
          DEFAULT_RELOAD_TIMEOUT,
          (fileSizeInKb / 250) * 1000,
        );

        setIsReady(true);
      };

      setup();
    }, [signedSrc]);

    const onLoad = useCallback(() => {
      setIsLoading(false);

      clearInterval(reloadInterval.current);
    }, []);

    const reload = useCallback(() => {
      if (!iframeRef.current) {
        return;
      }

      iframeRef.current.src += '';
    }, []);

    useEffect(() => {
      if (isReady) {
        reloadInterval.current = +setInterval(reload, reloadTimeout.current);
      }

      return () => {
        clearInterval(reloadInterval.current);
      };
    }, [isReady, reload]);

    const onRequestClose = useCallback(() => {
      modal.resolve();
      modal.hide();
    }, [modal]);

    const onAfterClose = useCallback(() => {
      modal.remove();
      onAfterCloseProp?.();
    }, [modal, onAfterCloseProp]);

    const renderFooter = useCallback(() => {
      return isReady && signature ? (
        <S.Footer>
          <S.SignatureContainer>
            <S.SignatureHeader>{t('shared:client')}</S.SignatureHeader>
            <S.SignatureHolder>
              <S.Signature>{signature.by}</S.Signature>
              <S.SignatureLine />
            </S.SignatureHolder>
            <S.SignatureName>{signature.by}</S.SignatureName>
            {signature.date ? (
              <S.SignatureDate>
                {t('shared:signed_on', {
                  date: dayjs(signature.date).format('lll'),
                })}
              </S.SignatureDate>
            ) : null}
          </S.SignatureContainer>
        </S.Footer>
      ) : null;
    }, [signature, t, isReady]);

    return (
      <Modal
        isFullWidth
        isLoading={isLoading}
        isOpen={modal.visible}
        maxWidth={'70vw'}
        minHeight={'70vh'}
        onAfterClose={onAfterClose}
        onRequestClose={onRequestClose}
        renderFooter={renderFooterProp || renderFooter}
        title={title}
      >
        <S.Container>
          {isReady ? (
            <S.IFrame onLoad={onLoad} ref={iframeRef} src={src}>
              {t('web-app:DocumentViewer.not_supported_message')}
            </S.IFrame>
          ) : null}
        </S.Container>
      </Modal>
    );
  },
);

export namespace DocumentViewerModal {
  export type Props = {
    document: FileApiModel;
    onAfterClose?: () => void;
    renderFooter?: GetComponentProps<typeof Modal>['renderFooter'];
    signature?: Signature;
    title: string;
  };

  interface Signature {
    by: string;
    date?: Date;
  }
}
